import React from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
} from '@mui/material';
import { TimePicker } from 'antd';
import { Dayjs } from 'dayjs';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';
import { ConfigItem, ConfigurationState } from '../types/configuration';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE } from '../../conf/constants';
import { validateDays, syncEditorToHtml, syncHtmlToEditor } from '../utils/configurationUtils';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface ConfigEditorProps {
  selectedConfig: ConfigItem;
  configState: ConfigurationState;
  setConfigState: (state: ConfigurationState) => void;
  isUpdating: boolean;
  onUpdate: () => void;
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({
  selectedConfig,
  configState,
  setConfigState,
  isUpdating,
  onUpdate,
}) => {
  const isTimeSpan = selectedConfig?.dataType === 'TimeSpan';
  const isBoolean = selectedConfig?.dataType === 'Boolean';
  const isEmailTemplate = selectedConfig?.type === 'template-html';
  const isJson = selectedConfig?.type === 'json';

  const handleEditorStateChange = (newEditorState: EditorState) => {
    const htmlValue = syncEditorToHtml(newEditorState);
    setConfigState({
      ...configState,
      editorState: newEditorState,
      htmlValue,
    });
  };

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHtml = e.target.value;
    const newEditorState = syncHtmlToEditor(newHtml);
    setConfigState({
      ...configState,
      htmlValue: newHtml,
      editorState: newEditorState,
    });
  };

  const handleValueChange = (value: string) => {
    setConfigState({
      ...configState,
      selectedValue: value,
    });
  };

  const handleDaysChange = (days: string) => {
    setConfigState({
      ...configState,
      selectedDays: days,
    });
  };

  const handleTimeChange = (time: Dayjs | null) => {
    setConfigState({
      ...configState,
      selectedTime: time,
    });
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        {selectedConfig.name}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {selectedConfig.description}
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Value
        </Typography>
        
        {isEmailTemplate ? (
          <>
            <Editor
              editorState={configState.editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName="wrapper-class"
              editorClassName="editor-class"
              toolbarClassName="toolbar-class"
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                inline: { inDropdown: true },
                list: { inDropdown: true },
                textAlign: { inDropdown: true },
                link: { inDropdown: true },
                history: { inDropdown: true },
              }}
              editorStyle={{
                height: '300px',
                border: `1px solid ${COLORS_ADMIN_PAGE.PRIMARY}`,
                borderRadius: '4px',
                padding: '10px',
              }}
            />
            <Box
              sx={{
                mt: 2,
                border: `1px solid ${COLORS_ADMIN_PAGE.PRIMARY}`,
                borderRadius: '4px',
                padding: '10px',
                minHeight: '200px',
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                width: '100%',
                overflow: 'auto',
                height: '200px',
              }}
            >
              <textarea
                value={configState.htmlValue}
                onChange={handleHtmlChange}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  lineHeight: 2,
                  background: 'transparent',
                  resize: 'none',
                  fontFamily: 'monospace',
                }}
              />
            </Box>
          </>
        ) : isTimeSpan ? (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={3}>
              <TextField
                fullWidth
                variant="outlined"
                label="Days"
                value={configState.selectedDays}
                onChange={(e) => handleDaysChange(e.target.value)}
                error={!validateDays(configState.selectedDays)}
                helperText={!validateDays(configState.selectedDays) ? 'Must be a non-negative number' : ''}
                type="number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    borderRadius: '4px',
                    '&.Mui-focused fieldset': {
                      borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                  },
                  '& .MuiFormLabel-root.Mui-focused': {
                    color: COLORS_ADMIN_PAGE.PRIMARY,
                  },
                }}
              />
            </Grid>
            <Grid item xs={9}>
              <TimePicker
                value={configState.selectedTime}
                onChange={handleTimeChange}
                format="HH:mm:ss"
                showNow={false}
                style={{
                  width: '100%',
                  height: '56px',
                }}
                className="custom-timepicker"
              />
            </Grid>
          </Grid>
        ) : isBoolean ? (
          <Select
            fullWidth
            variant="outlined"
            value={configState.selectedValue}
            onChange={(e) => handleValueChange(e.target.value as string)}
            sx={{
              height: '56px',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS_ADMIN_PAGE.PRIMARY,
              },
              '& .MuiSelect-icon': {
                color: COLORS_ADMIN_PAGE.PRIMARY,
              },
            }}
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        ) : isJson ? (
          <TextField
            fullWidth
            variant="outlined"
            value={typeof configState.selectedValue === 'string' ? configState.selectedValue : ''}
            onChange={(e) => handleValueChange(e.target.value)}
            multiline
            rows={15}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                },
              },
              '& .MuiFormLabel-root.Mui-focused': {
                color: COLORS_ADMIN_PAGE.PRIMARY,
              },
            }}
          />
        ) : (
          <TextField
            fullWidth
            variant="outlined"
            value={typeof configState.selectedValue === 'string' ? configState.selectedValue : ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={isTimeSpan ? 'DD:HH:MM:SS' : ''}
          />
        )}
      </Box>

      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onUpdate}
          sx={{ mt: 2, bgcolor: COLORS_CONFIG_PAGE.primary }}
        >
          {isUpdating ? <CircularProgress size={24} color="inherit" /> : 'Update'}
        </Button>
      </Box>
    </Box>
  );
};

export default ConfigEditor;